import axios from 'axios'
import tokenService from './token'
import { createApiUrl } from '../config/api'

const baseUrl = createApiUrl('/api/accounts/')
const userUrl = createApiUrl('/api/accounts/me/')
const adminUrl = createApiUrl('/api/accounts/admins/')
const passwordResetUrl = createApiUrl('/api/password_reset/')
const passwordResetConfirmUrl = createApiUrl('/api/password_reset/confirm/')
const passwordChangeUrl = createApiUrl('/api/accounts/change_password/')
const signOutEverywhereUrl = createApiUrl('/api/accounts/sign_out_everywhere/')
const emailChangeUrl = createApiUrl('/api/accounts/change_email/')
const activateUrl = createApiUrl('/api/accounts/activate/')
const confirmEmailUrl = createApiUrl('/api/accounts/confirm_email/')
const loginMethodUrl = createApiUrl('/api/accounts/login_method/')

const normalizePaginatedResponse = (data) => {
    if (Array.isArray(data)) {
        return {
            results: data,
            count: data.length,
            next: null,
            previous: null,
            total_pages: 1,
            current_page: 1,
        }
    }

    return data
}

const retrieve = async (query = '', page = 1) => {
    const queryString = [query, `page=${page}`].filter(Boolean).join('&')
    const res = await axios.get(`${baseUrl}?${queryString}`, tokenService.getAuthHeader())
    return normalizePaginatedResponse(res.data)
}

const retrieveSingle = async (id) => {
    const res = await axios.get(`${baseUrl}${id}/`, tokenService.getAuthHeader())
    return res.data
}

const getCurrent = async () => {
    const res = await axios.get(userUrl, tokenService.getAuthHeader())
    return res.data
}

const getAdmins = async () => {
    const res = await axios.get(adminUrl, tokenService.getAuthHeader())
    return res.data.results || res.data
}

const create = async (obj) => {
    const res = await axios.post(baseUrl, obj, tokenService.getAuthHeader())
    return res.data
}

const update = async (id, obj) => {
    const res = await axios.put(`${baseUrl}${id}/`, obj, tokenService.getAuthHeader())
    return res.data
}

const destroy = async (id, password) => {
    const res = await axios.delete(`${baseUrl}${id}/`, {
        // getAuthHeader() returns a whole request config ({ headers: {...} }), so it
        // is spread at the top level, not inside `headers:` -- nesting it sent a
        // literal "headers" header and no Authorization at all (issue #349).
        ...tokenService.getAuthHeader(),
        // Passwordless (Google) accounts must not send the key at all — the
        // frontend contract is key-omission, mirroring changeEmail (#317).
        data: {
            ...(password !== undefined && { password })
        }
    })
    return res
}

const changePassword = async (obj) => {
    const res = await axios.put(passwordChangeUrl, obj, tokenService.getAuthHeader())
    return res.data
}

const signOutEverywhere = async () => {
    await axios.post(signOutEverywhereUrl, {}, tokenService.getAuthHeader())
}

const changeEmail = async (obj) => {
    const res = await axios.put(emailChangeUrl, obj, tokenService.getAuthHeader())
    return res.data
}

const passwordReset = async (obj) => {
    const res = await axios.post(`${passwordResetUrl}`, obj, tokenService.getAuthHeader())
    return (res.status === 200)
}

const passwordResetConfirm = async (obj) => {
    const res = await axios.post(`${passwordResetConfirmUrl}`, obj, tokenService.getAuthHeader())
    return (res.status === 200)
}

const activateUser = async (id, token) => {
    const res = await axios.post(`${activateUrl}${id}/${token}/`, tokenService.getAuthHeader())
    return (res.status === 200)
}

const confirmEmailChange = async (id, token) => {
    const res = await axios.post(`${confirmEmailUrl}${id}/${token}/`)
    return res.data
}

// How an account signs in: 'google' (no usable password), 'password', or
// 'unknown'. Used only to turn a failed password login into a useful hint, so
// any failure here degrades to 'unknown' and the caller falls back to the
// generic credentials message.
const getLoginMethod = async (email) => {
    if(!email) return 'unknown'

    try {
        const res = await axios.post(loginMethodUrl, { email }, tokenService.getAnonHeader())
        return res.data.method || 'unknown'
    } catch {
        return 'unknown'
    }
}

const userService = { retrieve, retrieveSingle, getCurrent, getAdmins, create, update, destroy, changePassword, signOutEverywhere, passwordReset, passwordResetConfirm, changeEmail, activateUser, confirmEmailChange, getLoginMethod }

export default userService
